import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { cx } from '@/utils';
import DocumentPreviewModal from '@common/DocumentPreviewModal';
import SvgHistory from '@common/Icons/History';
import SearchearIcon from '@common/Icons/SearchearIcon';
import {
  ContentSearchType,
  PageSearchBlock,
  useSearchContentsQuery,
} from '@common/Providers/GraphqlProvider/generated';
import { t } from '@lingui/macro';
import { useModalSearchStore } from '@utils/store/store';

import { LexicalViewer } from '../../../../../components/common/LexicalEditorComponent/LexicalViewer';

interface SpotlightSearchProps {
  open?: boolean;
  searchBlock?: PageSearchBlock;
}

const SpotlightSearch = ({ open = false, searchBlock }: SpotlightSearchProps) => {
  const router = useRouter();
  const { onShow } = useModalSearchStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<{
    id: string;
    title: string;
    type: string;
  } | null>(null);
  const [modalClosing, setModalClosing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [open]);
  // Only execute query if there's a search term
  const { data, loading } = useSearchContentsQuery({
    variables: {
      includePagination: false,
      pagination: {
        skip: 0,
        take: 5,
      },
      where: {
        _and: [
          {
            status: {
              _in: ['active', 'published'],
            },
          },
          {
            _or: [
              {
                title: {
                  _contains: searchTerm,
                },
              },
              {
                description: {
                  _contains: searchTerm,
                },
              },
            ],
          },
        ],
      },
    },
    skip: !searchTerm.trim(), // Skip query if no search term
  });

  // Helper function to get category based on content type
  const getCategory = (type: string) => {
    const categoryMap: { [key: string]: string } = {
      [ContentSearchType.Event]: 'Eventos',
      [ContentSearchType.New]: 'Noticias',
      [ContentSearchType.Procedure]: 'Trámites',
      [ContentSearchType.Document]: 'Documentos',
      [ContentSearchType.Regulation]: 'Normativas',
      [ContentSearchType.Dynamic]: 'Páginas de navegación',
    };
    return categoryMap[type] || 'Información';
  };

  // Transform backend results to match our UI structure
  const searchResults =
    data?.contents?.map((content) => ({
      id: content.id,
      slug: content.slug,
      title: content.title,
      description: content.description || '', // Keep raw Lexical JSON for LexicalViewer
      category: getCategory(content.type),
      type: content.type,
      createdAt: content.publicationDate,
    })) || [];

  const filteredResults = searchTerm.trim() ? searchResults : [];

  // Load recent searches from localStorage on mount
  useEffect(() => {
    const loadRecentSearches = () => {
      try {
        const stored = localStorage.getItem('recentSearches');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) {
            setRecentSearches(parsed);
          }
        }
      } catch (error) {
        console.warn('Error loading recent searches:', error);
      }
    };

    loadRecentSearches();
  }, []);

  // Save search term to recent searches
  const saveToRecentSearches = (term: string) => {
    try {
      const trimmedTerm = term.trim();
      if (!trimmedTerm) return;

      const updated = [trimmedTerm, ...recentSearches.filter((item) => item !== trimmedTerm)].slice(
        0,
        4
      ); // Keep only the 4 most recent

      setRecentSearches(updated);
      localStorage.setItem('recentSearches', JSON.stringify(updated));
    } catch (error) {
      console.warn('Error saving recent search:', error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close SearchBar if DocumentPreviewModal is open or closing
      if (selectedDocument || modalClosing) {
        return;
      }

      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowResults(false);
        setIsFocused(false);
        onShow(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onShow, selectedDocument, modalClosing]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowResults(value.trim().length > 0);
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    if (searchTerm.trim().length > 0) {
      setShowResults(true);
    }
  };

  const handleResultClick = (result: (typeof searchResults)[0]) => {
    // Save the search term to recent searches
    saveToRecentSearches(searchTerm);

    // Handle document type differently - open modal preview
    if (
      result.type === ContentSearchType.Document ||
      result.type === ContentSearchType.Regulation
    ) {
      setSelectedDocument({
        id: result.id,
        title: result.title,
        type: result.type,
      });
      setShowResults(false);
      return;
    }

    // Navigate to respective pages based on content type
    // Use slug when available, fallback to id
    const identifier = result.slug || result.id;
    const navigationMap: { [key: string]: string } = {
      [ContentSearchType.Event]: `/events/${identifier}`,
      [ContentSearchType.New]: `/press-room/${identifier}`,
      [ContentSearchType.Dynamic]: `/dynamic/${identifier}`,
    };

    const navigationPath = navigationMap[result.type];

    if (navigationPath) {
      router.push(navigationPath);
    } else {
      // Fallback for unknown types
      console.log('No navigation defined for type:', result.type);
    }

    setShowResults(false);
    setIsFocused(false);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setShowResults(false);
    setIsFocused(true);
    // Don't refocus to prevent handleInputFocus from triggering
  };

  const handleDocumentClose = () => {
    setModalClosing(true);
    setSelectedDocument(null);
    // Return focus to input when closing document preview and restore focus state
    setTimeout(() => {
      inputRef.current?.focus();
      setIsFocused(true);
      setModalClosing(false);
    }, 150);
  };

  return (
    <>
      <div
        className={cx(
          'z-overlay fixed bottom-0 top-0 h-full w-full bg-black bg-opacity-10',
          open ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-full opacity-0'
        )}
      />
      <div
        className={cx(
          'fixed left-1/2 top-[130px] z-[60] -translate-x-1/2 transition-all duration-300 ease-in-out',
          'w-full px-5 md:w-96 md:min-w-[40em] md:px-0',
          open ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-full opacity-0'
        )}
      >
        <div className="relative" ref={containerRef}>
          <div
            className={cx(
              'relative flex h-[50px] items-center bg-white shadow-lg',
              'rounded-[12px] border transition-all duration-200',
              isFocused || searchTerm ? 'border-[1.5px] border-primary' : 'border'
            )}
          >
            <div className="flex items-center pl-4">
              <SearchearIcon width={18} height={18} className={'text-dimGray'} />
            </div>

            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              placeholder={
                searchBlock?.placeholder || `Buscar páginas o información relacionada....`
              }
              className={cx(
                'flex-1 rounded-lg px-2 py-4',
                'text-[16px] text-tundora placeholder-silver placeholder:font-normal',
                'bg-transparent outline-none placeholder:font-workSans placeholder:text-[16px]',
                'font-workSans font-medium focus:ring-0'
              )}
              autoComplete="off"
            />

            <div className="flex items-center pr-4">
              {searchTerm && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className={cx(
                    'bg-concrete p-1 text-dimGray hover:text-tundora',
                    'rounded-full transition-colors hover:bg-gray-100'
                  )}
                  aria-label={t`Limpiar búsqueda`}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 4L4 12M4 4L12 12"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Search Results Dropdown */}
          {showResults && (
            <div
              className={cx(
                'absolute left-0 right-0 top-full z-50 mt-1',
                'rounded-lg border border-silver bg-white shadow-lg',
                'max-h-96 overflow-y-auto'
              )}
            >
              {loading ? (
                <div className="px-4 py-8 text-center">
                  <div className="mb-2 text-sm text-dimGray">Buscando...</div>
                </div>
              ) : filteredResults.length > 0 ? (
                <>
                  <div className="border-b border-gray-100 px-4 py-2">
                    <span className="text-sm text-dimGray">
                      {filteredResults.length} resultado{filteredResults.length !== 1 ? 's' : ''}{' '}
                      encontrado{filteredResults.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="mx-4 py-2">
                    {filteredResults.map((result) => (
                      <button
                        key={result.id}
                        onClick={() => handleResultClick(result)}
                        className={cx(
                          'w-full px-1.5 py-1 text-left text-sm',
                          'transition-colors hover:bg-marine',
                          'group rounded-[4px] text-dimGray'
                        )}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 pr-2">
                            <div className="line-clamp-2 text-sm font-medium text-tundora group-hover:text-white">
                              {result.title}
                            </div>
                            <div className="mt-1 text-xs font-medium text-dimGray group-hover:text-white">
                              {result.category}
                            </div>
                            {result.description && (
                              <div className="mt-1 line-clamp-2 text-xs text-gray-500 group-hover:text-white">
                                <LexicalViewer value={result.description} />
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="px-4 py-8 text-center">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 48 48"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="mx-auto mb-3 text-gray-300"
                  >
                    <path
                      d="M45 45L34.95 34.95M40 22C40 32.4934 31.4934 41 21 41C10.5066 41 2 32.4934 2 22C2 11.5066 10.5066 3 21 3C31.4934 3 40 11.5066 40 22Z"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="mb-2 text-sm text-dimGray">No se encontraron resultados</div>
                  <div className="text-xs text-gray-400">
                    Intenta con otros términos de búsqueda
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Empty state suggestions */}
          {!searchTerm && isFocused && recentSearches.length > 0 && (
            <div
              className={cx(
                'absolute left-0 right-0 top-full z-50 mt-1',
                'rounded-lg border border-silver bg-white shadow-lg'
              )}
            >
              <div className="border-b border-gray-100 px-4 py-2">
                <span className="font-workSans text-[12px] font-normal text-silver">
                  Buscado recientemente
                </span>
              </div>
              <div className="mx-4 py-2">
                {recentSearches.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setSearchTerm(suggestion);
                      setShowResults(true);
                    }}
                    className={cx(
                      'w-full px-1.5 py-1 text-left text-sm',
                      'transition-colors hover:bg-marine',
                      'group rounded-[4px] text-dimGray'
                    )}
                  >
                    <div className="flex items-center gap-2 font-workSans text-[14px] font-medium text-tundora group-hover:text-white">
                      <SvgHistory className={'text-silver group-hover:text-white'} />
                      {suggestion}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Document Preview Modal */}
        <DocumentPreviewModal
          documentId={selectedDocument?.id || null}
          docTitle={selectedDocument?.title}
          documentType={
            selectedDocument?.type as ContentSearchType.Document | ContentSearchType.Regulation
          }
          onClose={handleDocumentClose}
        />
      </div>
    </>
  );
};

export default SpotlightSearch;
